/**
 * Eventos de comercio de Google Analytics 4 (SEO-05).
 *
 * Sin ellos GA ve visitas pero no ventas, y no hay forma de saber que canal
 * (Google, Instagram, directo) termina en una compra. Son los tres pasos del
 * embudo con los nombres que GA4 reconoce: `add_to_cart`, `begin_checkout` y
 * `purchase`. `purchase` se marca como evento clave en la propiedad.
 *
 * Los montos van en CLP con IVA, igual que en la tienda.
 */
import type { Carro, ItemPedido, ProductoResumen, RespuestaConfirmacionPedido } from '@bandu/compartido';

import { nombreConVariante } from '../utilidades/producto';
import { enviarEvento } from './analitica';

const MONEDA = 'CLP';

function articuloDeProducto(producto: ProductoResumen, cantidad: number) {
  return {
    item_id: producto.sku,
    item_name: nombreConVariante(producto),
    ...(producto.marca !== null ? { item_brand: producto.marca } : {}),
    ...(producto.etiquetaVariante !== null ? { item_variant: producto.etiquetaVariante } : {}),
    price: producto.precio,
    quantity: cantidad,
  };
}

function articuloDePedido(item: ItemPedido) {
  return {
    item_id: item.sku,
    item_name: item.nombre,
    price: item.precioUnitario,
    quantity: item.cantidad,
  };
}

export function registrarAgregadoAlCarro(producto: ProductoResumen, cantidad: number): void {
  enviarEvento('add_to_cart', {
    currency: MONEDA,
    value: producto.precio * cantidad,
    items: [articuloDeProducto(producto, cantidad)],
  });
}

export function registrarInicioCheckout(carro: Carro): void {
  enviarEvento('begin_checkout', {
    currency: MONEDA,
    value: carro.subtotal,
    items: carro.items.map((item) => articuloDeProducto(item.producto, item.cantidad)),
  });
}

/**
 * La compra, SOLO con el comprobante: es lo unico que prueba que el banco
 * aprobo y trae lo que de verdad se pago (nunca el carro, ver
 * `ConfirmacionPedido`). La referencia va como `transaction_id`, que es lo que
 * GA usa para descartar la misma compra si la pagina se recarga.
 */
export function registrarCompra(confirmacion: RespuestaConfirmacionPedido): void {
  const { comprobante } = confirmacion;
  if (!confirmacion.aprobado || comprobante === null) return;

  enviarEvento('purchase', {
    transaction_id: confirmacion.referencia,
    currency: MONEDA,
    value: confirmacion.total,
    items: comprobante.items.map(articuloDePedido),
  });
}
